import React, { useState, useEffect } from 'react';
import apiClient from '../config/axios';
import { logout } from '../utils/auth';
import './CFUDashboard.css';

const CFUDashboard = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendingEmailId, setSendingEmailId] = useState(null);
  const [loginDetails, setLoginDetails] = useState({
    username: '',
    password: '',
    portalUrl: ''
  });

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await apiClient.get('/api/clients/all');
      setClients(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching clients:', err);
      setError('Failed to load clients');
      setLoading(false);
    }
  };

  const handleSendSubscriptionForm = async (clientId) => {
    if (!clientId) {
      alert('Client ID is missing');
      return;
    }

    setSendingEmail(true);
    setSendingEmailId(clientId);
    
    try {
      console.log('Sending subscription form request for client:', clientId);
      const response = await apiClient.post('/api/emails/send-subscription-form-request', { clientId });
      console.log('Email sent successfully:', response.data);
      alert('Email sent successfully!');
      fetchClients(); // Refresh to update status
    } catch (err) {
      console.error('Error sending email:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to send email';
      console.error('Error details:', {
        status: err.response?.status,
        data: err.response?.data,
        message: err.message
      });
      alert(`Failed to send email: ${errorMessage}`);
    } finally {
      setSendingEmail(false);
      setSendingEmailId(null);
    }
  };

  const handleSendLoginDetails = async () => {
    if (!loginDetails.username || !loginDetails.password || !loginDetails.portalUrl) {
      alert('Please fill in all login details');
      return;
    }

    try {
      await apiClient.post('/api/emails/send-login-details', {
        clientId: selectedClient.id,
        loginDetails
      });
      alert('Login details email sent successfully!');
      setShowLoginModal(false);
      setLoginDetails({ username: '', password: '', portalUrl: '' });
      setSelectedClient(null);
      fetchClients(); // Refresh to update status
    } catch (err) {
      console.error('Error sending email:', err);
      alert(err.response?.data?.error || 'Failed to send email');
    }
  };

  // const openLoginModal = (client) => {
  //   setSelectedClient(client);
  //   setShowLoginModal(true);
  // };

  const openReceiptModal = (client) => {
    if (client.receipt_path) {
      // Open receipt in a new tab
      window.open(client.receipt_path, '_blank', 'noopener,noreferrer');
    } else {
      alert('No receipt uploaded for this client');
    }
  };

  const downloadReceipt = (receiptPath, clientName) => {
    // Create a temporary anchor element to trigger download
    const link = document.createElement('a');
    link.href = receiptPath;
    link.download = `receipt_${clientName.replace(/\s+/g, '_')}.pdf`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const getStatusBadge = (status) => {
    return (
      <span className={`status-badge ${status ? 'yes' : 'no'}`}>
        {status ? 'Yes' : 'No'}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="container">
          <h1>CFU Dashboard</h1>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="container">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h1>CFU Dashboard</h1>
          <div style={{ display: 'flex', gap: '10px' }}>
            <a href="/admin" className="btn btn-secondary">
              Upload Subscription Forms
            </a>
            <button className="btn btn-danger" onClick={logout}>
              Logout
            </button>
          </div>
        </div>
        
        {error && <div className="error-message">{error}</div>}

        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Estate</th>
                <th>Product</th>
                <th>Payment Type</th>
                <th>Amount</th>
                <th>Receipt</th>
                <th>Subscription Form Sent</th>
                <th>Login Details Sent</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {clients.length === 0 ? (
                <tr>
                  <td colSpan="12" style={{ textAlign: 'center' }}>
                    No clients found
                  </td>
                </tr>
              ) : (
                clients.map(client => (
                  <tr key={client.id}>
                    <td>{client.first_name} {client.last_name}</td>
                    <td>{client.email}</td>
                    <td>{client.phone_number}</td>
                    <td>{client.estate_name}</td>
                    <td>{client.product_name}</td>
                    <td>{client.payment_type}</td>
                    <td>₦{parseFloat(client.amount).toLocaleString()}</td>
                    <td>
                      {client.receipt_submitted ? (
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <button
                            className="btn btn-secondary"
                            onClick={() => openReceiptModal(client)}
                            style={{ fontSize: '11px', padding: '4px 8px' }}
                          >
                            View Receipt
                          </button>
                          <button
                            className="btn btn-primary"
                            onClick={() => downloadReceipt(client.receipt_path, `${client.first_name}_${client.last_name}`)}
                            style={{ fontSize: '11px', padding: '4px 8px' }}
                          >
                            Download
                          </button>
                        </div>
                      ) : (
                        <span className="status-badge no">No Receipt</span>
                      )}
                    </td>
                    <td>{getStatusBadge(client.subscription_form_sent)}</td>
                    <td>{getStatusBadge(client.login_details_sent)}</td>
                    <td>{formatDate(client.created_at)}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn btn-success"
                          onClick={() => {
                            console.log('Button clicked for client:', client.id, 'Type:', typeof client.id);
                            handleSendSubscriptionForm(client.id);
                          }}
                          disabled={client.subscription_form_sent === 1 || client.subscription_form_sent === true || (sendingEmail && sendingEmailId === client.id)}
                        >
                          {sendingEmail && sendingEmailId === client.id ? 'Sending...' : 'Send Subscription Form Request'}
                        </button>
                        {/* <button
                          className="btn btn-primary"
                          onClick={() => openLoginModal(client)}
                          disabled={!client.subscription_form_sent || client.login_details_sent}
                        >
                          Send Login Details
                        </button> */}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {showLoginModal && (
          <div className="modal" onClick={() => setShowLoginModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h3>Send Login Details</h3>
              <p>Client: {selectedClient?.first_name} {selectedClient?.last_name}</p>
              <p>Email: {selectedClient?.email}</p>
              
              <div className="form-group">
                <label htmlFor="username">Username</label>
                <input
                  type="text"
                  id="username"
                  value={loginDetails.username}
                  onChange={(e) => setLoginDetails({ ...loginDetails, username: e.target.value })}
                  placeholder="Enter username"
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  type="text"
                  id="password"
                  value={loginDetails.password}
                  onChange={(e) => setLoginDetails({ ...loginDetails, password: e.target.value })}
                  placeholder="Enter password"
                />
              </div>

              <div className="form-group">
                <label htmlFor="portalUrl">Portal URL</label>
                <input
                  type="url"
                  id="portalUrl"
                  value={loginDetails.portalUrl}
                  onChange={(e) => setLoginDetails({ ...loginDetails, portalUrl: e.target.value })}
                  placeholder="https://portal.example.com"
                />
              </div>

              <div className="modal-buttons">
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowLoginModal(false);
                    setLoginDetails({ username: '', password: '', portalUrl: '' });
                    setSelectedClient(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleSendLoginDetails}
                >
                  Send
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default CFUDashboard;
